import DayGridScenario from '../schema/DayGridScenario';
import { shouldGrayOutItem } from '../selectors/dayGridSelectors';
import getScenarioStore from '../selectors/getScenarioStore';
import SurfaceEventHandlerAdapter from '../utils/SurfaceEventHandlerAdapter';
import { observer } from 'mobx-react-lite';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import { getCharm } from 'owa-calendar-events-store';
import { CalendarEvent, renderAsAllDay } from 'owa-calendar-types';
import { ClientItemId } from 'owa-client-ids';
import { LIGHT_CALENDAR_COLOR_SCHEME } from 'owa-color-utils/lib/colorUtils';
import React from 'react';
import {
    createSchedulingConfig,
    isAttendeeItem,
    isSchedulingSelectionItem,
    SchedulingGrid,
    SchedulingGridScrollBehavior,
} from 'owa-calendar-scheduling-grid';
import {
    AutoScrollBehavior,
    inDayItemTemplate,
    ScrollToWorkingHoursBehavior,
    TimeMarkerTodayBehavior,
    WorkingHoursBehavior,
    allDayItemTemplate,
} from 'owa-calendar-surface-dategrid';
import {
    clickGridEvent,
    doubleClickGridEvent,
    rightClickGridEvent,
} from '../actions/publicActions';

const styles = require('./DayGridView.scss');
const classNames = require('classnames');

export interface DayGridViewProps {
    scenario: DayGridScenario;
    /** Enables gray shading of non-working hours and past days -- defaults to true */
    shadeWorkingHours?: boolean;
    selectedEventId?: ClientItemId;
}

const DayGridView = observer(function DayGridView(props: DayGridViewProps) {
    const { scenario, shadeWorkingHours } = props;
    const store = getScenarioStore(scenario);

    const onClickHandler = React.useCallback(SurfaceEventHandlerAdapter(scenario, clickGridEvent), [
        scenario,
    ]);
    const onDoubleClickHandler = React.useCallback(
        SurfaceEventHandlerAdapter(scenario, doubleClickGridEvent),
        [scenario]
    );
    const onContextMenuHandler = React.useCallback(
        SurfaceEventHandlerAdapter(scenario, rightClickGridEvent),
        [scenario]
    );

    const onKeydownHandler = React.useCallback(
        SurfaceEventHandlerAdapter(
            scenario,
            (scenario, id, parentFolderId, event) =>
                event.which === KeyCodes.enter && clickGridEvent(scenario, id)
        ),
        [scenario]
    );

    const shouldGrayOutItemForScenario = React.useCallback(
        (item: CalendarEvent) => shouldGrayOutItem(scenario, item),
        [scenario]
    );

    if (!store || !store.schedulingGridViewState) {
        return null;
    }

    const viewState = store.schedulingGridViewState;

    const config = {
        ...createSchedulingConfig(viewState),
        getItemConfig: () => ({
            itemsEventHandlers: {
                onClick: onClickHandler,
                onDoubleClick: onDoubleClickHandler,
                onContextMenu: onContextMenuHandler,
                onKeyDown: onKeydownHandler,
            },
            itemsDecorator: item => (
                <div
                    className={classNames(styles.gridItem, {
                        itemSelected: store.highlightedId && !shouldGrayOutItemForScenario(item),
                    })}
                    id={item.ItemId.Id}
                />
            ),
            template: (surfaceStore, model, state) => {
                const templateFunc = renderAsAllDay(model) ? allDayItemTemplate : inDayItemTemplate;
                return isAttendeeItem(model) || isSchedulingSelectionItem(model)
                    ? null
                    : templateFunc(
                          surfaceStore,
                          model,
                          shouldGrayOutItemForScenario(model)
                              ? LIGHT_CALENDAR_COLOR_SCHEME
                              : getCalendarEventColorScheme(model),
                          getCharm(model),
                          state
                      );
            },
        }),
    };

    const behaviors = [
        <AutoScrollBehavior key="autoscroll" />,
        <ScrollToWorkingHoursBehavior key="scrollto" store={viewState} />,
        <SchedulingGridScrollBehavior key="scheduling" viewState={viewState} />,
    ];

    if (shadeWorkingHours) {
        behaviors.push(
            <WorkingHoursBehavior
                key="workinghours"
                surfaceStore={viewState}
                modelDate={config.columnProvider.columnModels[0]}
            />
        );
    }

    const renderInDayBehaviors = [
        <TimeMarkerTodayBehavior key="time" columnProvider={config.columnProvider} />,
    ];

    return (
        <SchedulingGrid
            store={viewState}
            config={config}
            inDayBehaviors={behaviors}
            renderInDayBehaviors={renderInDayBehaviors}
        />
    );
});

DayGridView.defaultProps = {
    shadeWorkingHours: true,
};

export default DayGridView;
